import { useMutation, useQuery, useQueryClient } from "react-query"
import { createAttachmentUrl, createExistingProposalLink, createPreviewLink, disableFollowUp, getProposalEvents, getProposalPaymentSchedule, getProposals, getProposalSummary } from "./proposalsApi"
import { notification } from "antd"
import { DisableFollowUp } from "./proposalsModel"

export const useProposalsQuery = (jobId: number) => {
  return useQuery(['proposals', { jobId }], {
    queryFn: () => getProposals(jobId),
    enabled: !!jobId
  })
}

export const useProposalSummaryQuery = (jobId: number) => {
  return useQuery(['proposalSummary', { jobId }], {
    queryFn: () => getProposalSummary(jobId),
    enabled: !!jobId
  })
}

export const useProposalPaymentScheduleQuery = (proposalId: number) => {
  return useQuery(['proposalPaymentSchedule', { proposalId }], {
    queryFn: () => getProposalPaymentSchedule(proposalId),
    enabled: !!proposalId
  })
}

export const useProposalEventsQuery = (proposalId: number) => {
  return useQuery(['proposalEvents', { proposalId }], {
    queryFn: () => getProposalEvents(proposalId),
  })
}

export const useCreateExistingProposalLinkMutation = (onSuccess: (link: string) => void) => {
  return useMutation({
    mutationFn: (proposalId: number) => createExistingProposalLink(proposalId),
    onSuccess,
    onError: () => {
      notification.error({message: 'There was an error creating the proposal link.'})
    }
  })
}

export const useCreatePreviewLinkMutation = (onSuccess: (link: string) => void) => {
  return useMutation({
    mutationFn: (proposalId: number) => createPreviewLink(proposalId),
    onSuccess,
    onError: () => {
      notification.error({message: 'There was an error creating the preview link.'})
    }
  })
}

export const useCreateAttachmentUrlMutation = (onSuccess: (url: string) => void) => {
  return useMutation({
    mutationFn: (attachmentId: string) => createAttachmentUrl(attachmentId),
    onSuccess: x => {
      onSuccess(x)
    },
  })
}

export const useProposalsInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidate: (jobId: number) => queryClient.invalidateQueries(['proposals', { jobId }])
  }
}

export const useProposalSummaryInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidate: (jobId: number) => queryClient.invalidateQueries(['proposalSummary', { jobId }])
  }
}

export const useProposalPaymentScheduleInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidate: (proposalId: number) => queryClient.invalidateQueries(['proposalPaymentSchedule', { proposalId }])
  }
}

export const useProposalEventsInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidate: (proposalId: number) => queryClient.invalidateQueries(['proposalEvents', { proposalId }])
  }
}

export const useDisableFollowUpMutation = (onSuccess: () => void) => {
  return useMutation({
    mutationFn: ({proposalId, customerUserId}: DisableFollowUp) => disableFollowUp(proposalId, customerUserId),
    onSuccess,
    onError: () => {
      notification.error({message: 'There was an error disabling the follow-up.'})
    }
  })
}